import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import {
  MICROSOFT_SCOPES,
  MICROSOFT_CLIENT_ID,
  MICROSOFT_TENANT_ID,
  MICROSOFT_REDIRECT_URl,
} from 'src/config';

interface MicrosoftAuthPopupProps {
  Provider?: string;
  id?: string;
}
export const useMicroSoftAuthPopup = ({ Provider = '', id = '' }: MicrosoftAuthPopupProps = {}) => {
  // MICROSOFT OAUTH2.0
  const MSAuthConfig = {
    clientId: MICROSOFT_CLIENT_ID,
    tenantId: MICROSOFT_TENANT_ID,
    redirectUri: MICROSOFT_REDIRECT_URl,
    scopes: MICROSOFT_SCOPES,
  };

  const navigate = useNavigate();
  const { t } = useTranslate('translation');
  const { state: channelState, handleMicrosoftDetail } = useChannelContext();
  const [isMSAccVerified, setIsMSAccVerified] = useState(false);

  const { emailProviderType, microsoftEmailDetails } = channelState;

  const stateObject = encodeURIComponent(
    JSON.stringify({ ...(!id && { step: 2 }), ...(id && { id, provider: Provider }) })
  );

  const loginWithMicrosoft = async () => {
    const authUrl = `https://login.microsoftonline.com/${MSAuthConfig.tenantId}/oauth2/v2.0/authorize?client_id=${MSAuthConfig.clientId}&response_type=code&redirect_uri=${encodeURIComponent(
      MSAuthConfig.redirectUri
    )}&response_mode=query&scope=${encodeURIComponent(MSAuthConfig.scopes)}&prompt=select_account&state=${stateObject}`;

    window.location.href = authUrl;
  };

  const handleMicrosoftAuthRedirect = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const stateParam = urlParams.get('state');

    if (code) {
      interface ParsedState {
        step?: number;
      }
      let parsedState: { step?: number; provider?: string; id?: string } = {};
      try {
        parsedState = JSON.parse(decodeURIComponent(stateParam || '{}'));
      } catch (err) {
        console.warn('Invalid state param:', err);
      }

      handleMicrosoftDetail(code);
      toast.success(`${t(emailProviderType.toUpperCase())} ${t('ACCOUNT_VERIFIED_SUCCESSFULLY')}`);

      // 👇 Redirect to step from state (step=2)
      if (!parsedState.provider) {
        if (parsedState.step) {
          navigate(`/channel/step?step=${parsedState.step}`, { replace: true });
        } else {
          // fallback
          navigate('/channel/step', { replace: true });
        }
      } else {
        navigate(`/channel/${parsedState.id}/edit`, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    handleMicrosoftAuthRedirect();
  }, [handleMicrosoftAuthRedirect]);

  useEffect(() => {
    if (emailProviderType === 'microsoft' && microsoftEmailDetails.code) {
      setIsMSAccVerified(true);
    }
  }, [emailProviderType, microsoftEmailDetails.code]);

  return { loginWithMicrosoft, handleMicrosoftAuthRedirect, isMSAccVerified };
};
